<template>
  <dt-button kind="unstyled" class="dp3-user-menu" label-class="d-d-flex">
    <dt-tooltip :message="userProfile?.status_message">
      <template #anchor="{ attrs }">
        <div class="dp3-status" v-bind="attrs">
          <div class="dp3-status--emoji">{{ userProfile.emoji_icon }}</div>
          <div class="dp3-status--text d-truncate d-fco75">
            {{ userProfile.status_message }}
          </div>
        </div>
      </template>
    </dt-tooltip>
    <div>
      <dt-dropdown navigation-type="arrow-keys">
        <template #anchor="{ attrs }">
          <ContactAvatar v-if="currentUserAvatarData?.props" v-bind="{ ...attrs, ...currentUserAvatarData.props }" />
        </template>
        <template #list="{ close }">
          <dt-list-item
            v-for="item in dropdownItems"
            :key="item.id"
            role="menuitem"
            navigation-type="arrow-keys"
            @click="
              () => {
                close();
                if (item.name === 'Preferences') {
                  openPreferencesModal();
                }
              }
            "
          >
            {{ item.name }}
          </dt-list-item>
        </template>
      </dt-dropdown>
    </div>

    <!-- Preferences Modal -->
    <PreferencesModal v-model="isPreferencesModalOpen" />
  </dt-button>
</template>

<script setup>
import { computed, ref } from 'vue';
import { DtAvatar, DtDropdown, DtListItem, DtButton, DtTooltip } from '@dialpad/dialtone/vue3';
import PreferencesModal from './preferences/PreferencesModal.vue';

import { useDataStore } from '@/store/useDataStore';
import { useUserStore } from '@/store/useUserStore';
import { useAvatarUtilities } from '@/composables/ui/useAvatarUtilities';
import ContactAvatar from '@/components/ContactAvatar.vue';

//  Get stores
const userStore = useUserStore();
const dataStore = useDataStore();
const selectedUserId = computed(() => userStore.userId);

// Initialize avatar utilities for current user
const { getCurrentUserAvatarData, createAvatarPropsWithPresence } = useAvatarUtilities(selectedUserId.value);

//  Get current user from contacts with is_system_user flag
const userProfile = computed(() => {
  // First try to find by ID (UUID) directly
  let contact = dataStore.contactsData.contacts.find(
    contact => contact.id === selectedUserId.value && contact.is_system_user
  );

  // If not found, try the old way with userId
  if (!contact) {
    contact = dataStore.contactsData.contacts.find(
      contact => contact.userId === selectedUserId.value && contact.is_system_user
    );
  }

  return contact;
});

// Get current user avatar data using composable with presence
const currentUserAvatarData = computed(() => {
  if (!userProfile.value) return null;

  // Use createAvatarPropsWithPresence to include presence status
  return {
    props: createAvatarPropsWithPresence(userProfile.value, userProfile.value.status, 'md'),
  };
});

// Dropdown menu items
const dropdownItems = ref([
  { id: 1, name: 'Profile' },
  { id: 2, name: 'Preferences' },
  { id: 3, name: 'Devices' },
  { id: 4, name: 'Sign Out' },
]);

// Preferences modal state
const isPreferencesModalOpen = ref(false);

// Open preferences modal when preferences option is clicked
function openPreferencesModal() {
  isPreferencesModalOpen.value = true;
}
</script>

<style lang="less" scoped>
.dp3-user-menu {
  display: flex;
  align-items: center;
  gap: var(--dt-space-300);
}

.dp3-status {
  display: flex;
  align-items: center;
  gap: var(--dt-space-200);
  max-width: 200px;

  &--emoji {
    font-size: var(--dt-font-size-300);
  }

  &--text {
    color: var(--dt-color-foreground-secondary);
    font-size: var(--dt-font-size-200);
  }
}
</style>
